import { useState } from 'react';
import { useNavigate } from 'react-router';
import { State } from 'ts-fsrs';
import { db } from '../../db/index.js';
import { loadDeckCards, isActive } from '../../scheduler/queue.js';
import { shuffle } from '../../cardTypes/mcq.js';
import { useAllTags } from '../notes/TagInput.jsx';
import Sheet from '../../components/Sheet.jsx';
import Button from '../../components/Button.jsx';
import { ChoiceCards, Chips, Group, Stepper, Toggle } from '../../components/Field.jsx';
import { toast } from '../../components/Toaster.jsx';
import { saveCustomSession } from '../study/customSession.js';
import styles from './DeckSheets.module.scss';

const DAY = 86400000;

const MODES = [
  { id: 'forgotten', icon: 'undo', title: 'Go over mistakes', desc: 'Cards you got wrong recently.', days: 'Got wrong in the last', reschedule: true },
  { id: 'ahead', icon: 'calendar', title: 'Study ahead', desc: 'Reviews coming up soon, before they’re due.', days: 'Due in the next', reschedule: true },
  { id: 'new', icon: 'sparkle', title: 'More new cards', desc: 'Go past today’s new card limit.', reschedule: true },
  { id: 'tag', icon: 'tag', title: 'Cards with a tag', desc: 'Practice one topic.', tag: true, reschedule: false },
  { id: 'flagged', icon: 'flag', title: 'Flagged cards', desc: 'Cards you marked to look at again.', reschedule: false },
  { id: 'cram', icon: 'shuffle', title: 'Everything, shuffled', desc: 'All cards in random order. Great before a test.', reschedule: false },
];

async function buildList(deckId, mode, { days, tag, limit }) {
  const now = Date.now();
  const { cards } = await loadDeckCards(db, deckId);
  let list = cards.filter((c) => isActive(c, now) || mode === 'flagged');
  const st = (c) => c.fsrs?.state ?? State.New;
  switch (mode) {
    case 'forgotten':
      list = list.filter((c) => c.lastRating === 1 && c.lastReviewedAt >= now - days * DAY);
      break;
    case 'ahead':
      list = list.filter((c) => st(c) === State.Review && c.due < now + days * DAY).sort((a, b) => a.due - b.due);
      break;
    case 'new':
      list = list.filter((c) => st(c) === State.New).sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0) || a.ord - b.ord);
      break;
    case 'tag': {
      const notes = await db.notes.where('tags').equals(tag).primaryKeys();
      const set = new Set(notes);
      list = shuffle(list.filter((c) => set.has(c.noteId)), String(now));
      break;
    }
    case 'flagged':
      list = list.filter((c) => c.flag && !c.deletedAt);
      break;
    default:
      list = shuffle(list, String(now));
  }
  return list.slice(0, limit).map((c) => c.id);
}

export default function CustomStudySheet({ open, onClose, deck }) {
  const navigate = useNavigate();
  const tags = useAllTags();
  const [mode, setMode] = useState('forgotten');
  const [days, setDays] = useState(1);
  const [chosenTag, setTag] = useState('');
  const [limit, setLimit] = useState(50);
  const def = MODES.find((m) => m.id === mode);
  const [reschedule, setReschedule] = useState(def.reschedule);
  const tag = chosenTag || tags[0] || '';

  const chooseMode = (id) => {
    setMode(id);
    setReschedule(MODES.find((m) => m.id === id).reschedule);
  };

  const start = async (e) => {
    e.preventDefault();
    const ids = await buildList(deck.id, mode, { days: Number(days) || 1, tag, limit: Math.max(1, Number(limit) || 50) });
    if (!ids.length) {
      toast.info('No cards match that right now. Try another option.');
      return;
    }
    const id = saveCustomSession({
      title: `${deck.name} · ${def.title}`,
      cardIds: ids,
      reschedule,
      returnTo: `/deck/${deck.id}`,
    });
    onClose();
    navigate(`/study?custom=${id}`);
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Extra practice"
      subtitle="Study outside of today’s plan."
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button size="lg" variant="primary" type="submit" form="custom-study" icon="play" disabled={def.tag && !tag}>
            Start
          </Button>
        </>
      }
    >
      <form id="custom-study" onSubmit={start}>
        <ChoiceCards
          label="What do you want to practice?"
          value={mode}
          onChange={chooseMode}
          options={MODES.map((m) => ({ value: m.id, label: m.title, description: m.desc, icon: m.icon }))}
          size="sm"
        />
        {def.tag ? (
          tags.length ? (
            <Chips label="Tag" value={tag} onChange={setTag} options={tags.map((t) => ({ value: t, label: t }))} />
          ) : (
            <p className={styles.tagEmpty}>You haven’t tagged any cards yet. Add tags while editing a card.</p>
          )
        ) : null}
        <Group>
          {def.days ? (
            <Stepper label={def.days} value={days} onChange={setDays} min={1} max={365} suffix={Number(days) === 1 ? 'day' : 'days'} />
          ) : null}
          <Stepper label="Up to" value={limit} onChange={setLimit} min={1} max={9999} step={10} suffix="cards" />
          <Toggle
            label="Count toward my schedule"
            hint={reschedule ? 'Your answers change when cards come back.' : 'Just practice. Your schedule stays the same.'}
            checked={reschedule}
            onChange={setReschedule}
          />
        </Group>
      </form>
    </Sheet>
  );
}
